// Listing Coverage logic shared by today's view (BranchOverview/
// MFCoverageView) and the redesigned tab (BranchesV2): which stock units have
// no listing, which listings have too few photos, grouped by branch.

import { sellSpeedCategory } from './sellSpeedCategory';

const UNKNOWN = 'Unknown';

/** Asking price, else the sales value, else 0. */
export const unitValue = (u) => u.Best_Sell_Price || u.SALES_VALUE || 0;

/** Inventory units with a stock number that no matrix row lists (string compare). */
export const findUnlistedUnits = (invUnits, matrixRows) => {
    if (!invUnits?.length) return [];
    const listed = new Set((matrixRows || []).map((r) => String(r.stock_number || '')));
    return invUnits.filter((u) => u.StockNumber && !listed.has(String(u.StockNumber)));
};

export const coverageTotals = (unlisted) => ({
    count: unlisted.length,
    value: unlisted.reduce((s, u) => s + unitValue(u), 0),
    verySlow: unlisted.filter((u) => sellSpeedCategory(u)?.startsWith('Very Slow')).length,
});

/** Unlisted units per branch with value and sell-speed mix, most first. */
export const groupUnlistedByBranch = (unlisted) => {
    const map = {};
    unlisted.forEach((u) => {
        const b = u.BranchName || UNKNOWN;
        if (!map[b]) map[b] = { branch: b, count: 0, value: 0, days: 0, verySlow: 0, slow: 0, normal: 0, fast: 0, total: 0 };
        map[b].count += 1;
        map[b].value += unitValue(u);
        map[b].days += u.DaysInStock || 0;
        map[b].total += 1;
        const speed = sellSpeedCategory(u);
        if (speed?.startsWith('Very Slow')) map[b].verySlow += 1;
        else if (speed?.startsWith('Slow')) map[b].slow += 1;
        else if (speed?.startsWith('Normal')) map[b].normal += 1;
        else if (speed?.startsWith('Fast')) map[b].fast += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
};

/** Every unlisted unit of one branch, oldest first. */
export const unlistedForBranch = (unlisted, branch) => unlisted
    .filter((u) => (u.BranchName || UNKNOWN) === branch)
    .sort((a, b) => (b.DaysInStock || 0) - (a.DaysInStock || 0));

/** Listings with 0 photos or fewer than the make/model/year average, fewest first. */
export const findPhotoIssues = (matrixRows) => {
    const rows = matrixRows || [];
    if (!rows.length) return [];
    const keyOf = (r) => `${r.make}||${r.model}||${r.year ?? ''}`;
    const groupSum = {};
    const groupCount = {};
    rows.forEach((r) => {
        if (r.photo_count == null) return;
        const key = keyOf(r);
        groupSum[key] = (groupSum[key] || 0) + r.photo_count;
        groupCount[key] = (groupCount[key] || 0) + 1;
    });
    const groupAvg = {};
    Object.keys(groupSum).forEach((k) => { groupAvg[k] = groupSum[k] / groupCount[k]; });
    return rows
        .filter((r) => r.photo_count != null)
        .map((r) => {
            const avg = groupAvg[keyOf(r)] ?? null;
            const belowAvg = avg != null && r.photo_count < avg;
            return { ...r, avg_photos: avg != null ? Math.round(avg * 10) / 10 : null, belowAvg };
        })
        .filter((r) => r.photo_count === 0 || r.belowAvg)
        .sort((a, b) => (a.photo_count ?? 0) - (b.photo_count ?? 0));
};

/** Photo issues per branch with the no-photo count, most first. */
export const groupPhotoIssuesByBranch = (photoRows) => {
    const map = {};
    photoRows.forEach((r) => {
        const b = r.branch || UNKNOWN;
        if (!map[b]) map[b] = { branch: b, count: 0, noPhotos: 0 };
        map[b].count += 1;
        if (r.photo_count === 0) map[b].noPhotos += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
};

/**
 * What the coverage view can show. A failed /pricing-matrix is an error: with
 * no listings to compare against, every unit would read "Not listed".
 * Order: matrix error, inventory error, loading, empty inventory, ready.
 */
export const coverageState = ({
    inventoryLoaded, inventoryLoading, inventoryError, units, matrixData, matrixLoading, matrixError,
}) => {
    if (matrixError && !matrixLoading) return 'matrixError';
    if (inventoryError && !inventoryLoading && !(units || []).length) return 'inventoryError';
    if (matrixLoading || !matrixData || inventoryLoading || !inventoryLoaded) return 'loading';
    if (!(units || []).length) return 'empty';
    return 'ready';
};
